import { derived, get, writable } from "svelte/store";
import axios from "../config/axios";
import { advice } from "./advice";
import { mattressWidth as mattressWidthStore } from "./mattressWidth";

const { subscribe, update } = writable({
  questionList: [],
  activeQuestions: [],
});

/**
 * Initialize the questionnaire and cache the API result.
 */
function init() {
  // Get cached question list
  const cachedQuestionList = JSON.parse(
    localStorage.getItem("questionnaire-questions")
  );

  // Set questions from cache or from API request
  if (!cachedQuestionList || cachedQuestionList.expiresOn < Date.now()) {
    localStorage.removeItem("questionnaire-questions");

    axios.get("/questionnaires/auping-matras-keuzehulp").then((response) => {
      const questionList = response.data.questions;

      localStorage.setItem(
        "questionnaire-questions",
        JSON.stringify({
          expiresOn: Date.now() + +import.meta.env.VITE_CACHE_EXPIRY_TIME,
          value: questionList,
        })
      );

      update((questionnaire) =>
        Object.assign(questionnaire, {
          questionList: filterConfiguratorQuestions(questionList),
        })
      );

      update((questionnaire) =>
        Object.assign(questionnaire, {
          activeQuestions: initActiveQuestions(questionnaire),
        })
      );
    });
  } else {
    update((questionnaire) =>
      Object.assign(questionnaire, {
        questionList: filterConfiguratorQuestions(cachedQuestionList.value),
      })
    );

    update((questionnaire) =>
      Object.assign(questionnaire, {
        activeQuestions: initActiveQuestions(questionnaire),
      })
    );
  }
}

/**
 * Filters
 *
 * @param questionList
 * @returns
 */
function filterConfiguratorQuestions(questionList) {
  const domain = window.location.host;
  const mattressWidth = get(mattressWidthStore);

  if (domain.includes(import.meta.env.VITE_CONFIGURATOR_URL) && mattressWidth) {
    questionList = questionList.map((question) => {
      if (question.slug !== "mattress-type") return question;

      if (mattressWidth > 140) {
        question.answers = question.answers.filter(
          (answer) => answer.value !== "one-person-mattress"
        );
      } else {
        question.answers = question.answers.filter(
          (answer) => answer.value === "one-person-mattress"
        );
      }

      return question;
    });
  }

  return questionList;
}

/**
 * Initialize the active questions.
 *
 * @param questionnaire
 * @returns
 */
function initActiveQuestions(questionnaire) {
  return questionnaire.questionList
    .filter((_, index) => index <= 3)
    .map((question) => Object.assign(question, { selectedAnswer: null }));
}

/**
 * Set an answer.
 *
 * @param id
 * @param questionId
 * @param answerId
 */
function setAnswer(question, answer) {
  advice.resetAdvice();

  update((questionnaire) => {
    const activeQuestions = [...questionnaire.activeQuestions];

    const activeQuestion = activeQuestions.find(
      (activeQuestion) => question.id === activeQuestion.id
    );

    const previousAnswer = activeQuestion.selectedAnswer;
    activeQuestion.selectedAnswer = answer;

    // Set information prompt display for the answered question
    if (activeQuestion.slug === "new-mattress-reason") {
      switch (answer.slug) {
        case "physical-complaints":
          activeQuestion.additionalInfo = {
            slug: "physical-complaints",
            urls: {
              "nl-nl": "https://www.auping.com/nl/store-locator",
              "nl-be": "https://www.auping.com/nl-be/store-locator",
              "fr-be": "https://www.auping.com/fr-be/store-locator",
              "de-de": "https://www.auping.com/de/store-locator",
            },
          };
          break;
        case "new-bed":
          if (
            !window.location.host.includes(
              import.meta.env.VITE_CONFIGURATOR_URL
            )
          ) {
            activeQuestion.additionalInfo = {
              slug: "new-bed",
              urls: {
                "nl-nl": "https://configurator.auping.com/nl-nl/",
                "nl-be": "https://configurator.auping.com/nl-be/",
                "fr-be": "https://configurator.auping.com/fr-be/",
                "de-de": "https://configurator.auping.com/de-de/",
              },
            };
          } else {
            activeQuestion.additionalInfo = null;
          }
          break;
        default:
          activeQuestion.additionalInfo = null;
          break;
      }
    }

    // Remove inactive questions
    if (
      !!previousAnswer &&
      previousAnswer["follow_up_question_id"] !==
        answer["follow_up_question_id"]
    ) {
      const activeQuestionIndex = activeQuestions.findIndex(
        (q) => activeQuestion.id === q.id
      );

      activeQuestions.splice(
        activeQuestionIndex + 1,
        activeQuestions.length - activeQuestionIndex - 1
      );
    }

    // Add new follow up questions
    if (
      !!activeQuestion.selectedAnswer &&
      !!activeQuestion.selectedAnswer["follow_up_question_id"] &&
      !activeQuestions.some(
        (q) => activeQuestion.selectedAnswer["follow_up_question_id"] === q.id
      )
    ) {
      const questionToAdd = questionnaire.questionList.find(
        (question) =>
          question.id === activeQuestion.selectedAnswer["follow_up_question_id"]
      );

      questionToAdd.selectedAnswer = null;
      activeQuestions.push(questionToAdd);
    }

    // Update budget question answers according to mattress type answer.
    const budgetQuestion = activeQuestions.find(
      (q) => q.slug === "budget-two-persons"
    );

    if (budgetQuestion) {
      const budgetType = activeQuestions.find((q) => q.slug === "mattress-type")
        .selectedAnswer.slug;

      const staticQuestionList = JSON.parse(
        localStorage.getItem("questionnaire-questions")
      ).value;

      budgetQuestion.answers = staticQuestionList
        .find((q) => q.slug === budgetQuestion.slug)
        .answers.filter((answer) => answer.type === budgetType || !answer.type);
    }

    // Reassign answers in the store
    return Object.assign(questionnaire, {
      activeQuestions,
    });
  });
}

/**
 * Export the questionnaire store.
 */
export const questionnaire = {
  subscribe,
  init,
  setAnswer,
};

/**
 * Exports the 'completed' state of the questionnaire.
 */
export const questionnaireComplete = derived(questionnaire, ($questionnaire) =>
  $questionnaire.activeQuestions.every((question) => !!question.selectedAnswer)
);
